import { useState, useEffect, useRef } from 'react';
import { X, Search, Table, Database, FileJson, BarChart3, Layers, Eye } from 'lucide-react';
import useFlowStore from '../../stores/useFlowStore';
import { useReactFlow } from 'reactflow';

const getNodeIcon = (type) => {
  switch (type) {
    case 'table': return Database;
    case 'collection': return FileJson;
    case 'fact': return BarChart3;
    case 'dimension': return Layers;
    case 'view': return Eye;
    default: return Table;
  }
};

const getNodeColor = (type) => {
  const colors = {
    table: '#3b82f6',
    collection: '#10b981',
    fact: '#f59e0b',
    dimension: '#8b5cf6',
    view: '#06b6d4',
  };
  return colors[type] || '#6b7280';
};

const SearchModal = ({ isOpen, onClose }) => {
  const { nodes } = useFlowStore();
  const { fitView, setCenter } = useReactFlow();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setResults([]);
      return;
    }

    const term = searchTerm.toLowerCase();
    const matches = [];

    nodes.forEach(node => {
      const nodeName = node.data?.name?.toLowerCase() || '';
      const nodeType = node.type || '';
      
      // Search by entity name
      if (nodeName.includes(term)) {
        matches.push({
          id: node.id,
          type: 'entity',
          nodeType,
          name: node.data.name,
          highlight: 'Entity name',
          position: node.position,
          score: nodeName.indexOf(term) === 0 ? 100 : 50, // Exact match gets higher score
        });
      }

      // Search by field names
      const fields = node.data?.fields || [];
      fields.forEach((field, index) => {
        const fieldName = field.name?.toLowerCase() || '';
        if (fieldName.includes(term)) {
          matches.push({
            id: `${node.id}_field_${index}`,
            nodeId: node.id,
            type: 'field',
            nodeType,
            entityName: node.data.name,
            fieldName: field.name,
            fieldType: field.type,
            highlight: `Field in ${node.data.name}`,
            position: node.position,
            score: fieldName.indexOf(term) === 0 ? 90 : 40,
          });
        }
      });
    });

    // Sort by score (highest first)
    matches.sort((a, b) => b.score - a.score);
    
    setResults(matches.slice(0, 10)); // Limit to 10 results
    setSelectedIndex(0);
  }, [searchTerm, nodes]);

  const handleSelect = (result) => {
    const nodeId = result.type === 'entity' ? result.id : result.nodeId;
    const node = nodes.find(n => n.id === nodeId);
    
    if (node) {
      // Center on the node with zoom
      setCenter(node.position.x + 125, node.position.y + 75, { zoom: 1.5, duration: 800 });
      
      // Select the node
      useFlowStore.getState().setSelectedNode(node);
    }
    
    onClose();
    setSearchTerm('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results.length > 0) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-[70] p-4 pt-20 animate-fadeIn">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full animate-scaleIn">
        {/* Search Input */}
        <div className="relative border-b border-gray-200 dark:border-gray-700">
          <Search className="absolute left-4 top-4 text-gray-400" size={20} />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search entities and fields..."
            className="w-full pl-12 pr-12 py-4 text-lg bg-transparent border-none focus:outline-none focus:ring-0 text-gray-900 dark:text-white placeholder-gray-400"
          />
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-96 overflow-y-auto">
          {searchTerm && results.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <p>No results found for "{searchTerm}"</p>
              <p className="text-sm mt-2">Try searching by entity or field name</p>
            </div>
          ) : results.length > 0 ? (
            <div className="py-2">
              {results.map((result, index) => {
                const Icon = getNodeIcon(result.nodeType);
                const color = getNodeColor(result.nodeType);
                const isSelected = index === selectedIndex;

                return (
                  <div
                    key={result.id}
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`px-4 py-3 cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-blue-50 dark:bg-blue-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="p-2 rounded-lg flex-shrink-0"
                        style={{ backgroundColor: `${color}20` }}
                      >
                        <Icon size={20} style={{ color }} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        {result.type === 'entity' ? (
                          <>
                            <div className="font-semibold text-gray-900 dark:text-white truncate">
                              {result.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {result.highlight} • {result.nodeType}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="font-semibold text-gray-900 dark:text-white truncate">
                              {result.fieldName}
                              <span className="text-gray-500 dark:text-gray-400 font-normal ml-2 text-sm">
                                {result.fieldType}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {result.highlight}
                            </div>
                          </>
                        )}
                      </div>

                      {isSelected && (
                        <div className="text-xs text-blue-600 dark:text-blue-400 flex-shrink-0">
                          Press Enter ↵
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <Search className="mx-auto mb-3 opacity-50" size={40} />
              <p className="font-medium">Search for entities and fields</p>
              <p className="text-sm mt-2">Type to find tables, collections, or specific fields</p>
              <div className="mt-4 text-xs space-y-1">
                <p>• Use ↑↓ arrows to navigate</p>
                <p>• Press Enter to jump to selected</p>
                <p>• Press Esc to close</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {results.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
            <span>{results.length} result{results.length !== 1 ? 's' : ''} found</span>
            <span className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">↑↓</kbd>
              navigate
              <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">↵</kbd>
              select
              <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">esc</kbd>
              close
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchModal;
